import {HttpClientError} from "../http/HttpClientError";
import ensureErrorType from "./ensureErrorType.js";

interface ErrorEntry {
	cause: string | undefined;
	columnNumber?: number;
	lineNumber?: number;
	message: string;
	name: string;
	stack: string;
	url?: string;
	userAgent?: string;
	viewportSize?: string;
}

function connectionDiagnostics(): Pick<ErrorEntry, "online" | "connectionType" | "downlink" | "rtt"> {
	const nav = navigator as Navigator & { connection?: NavigatorConnection };
	const connection = nav.connection;
	return {
		online: typeof nav.onLine === "boolean" ? nav.onLine : undefined,
		connectionType: connection?.effectiveType,
		downlink: connection?.downlink,
		rtt: connection?.rtt
	};
}

function httpDiagnostics(error: Error): Pick<ErrorEntry, "httpStatus" | "failedUrl" | "failedMethod" | "detail"> {
	if (!(error instanceof HttpClientError)) {
		return {};
	}

	return {
		httpStatus: error.status,
		failedUrl: error.problemDetails.instance,
		failedMethod: error.request?.method,
		detail: error.problemDetails.detail ?? error.problemDetails.title
	};
}

function errorToErrorEntry(error: Error): ErrorEntry {
	return {
		name: error.name,
		message: error.message,
		stack: error.stack || error.toString(),
		cause: error.cause == null ? undefined : JSON.stringify(errorToErrorEntry(ensureErrorType(error.cause))),
		url: window.location.href,
		userAgent: navigator.userAgent,
		viewportSize: `${window.innerWidth}x${window.innerHeight}`,
		...connectionDiagnostics(),
		...httpDiagnostics(error)
	}
}

interface ErrorEntry {
	cause: string | undefined;
	columnNumber?: number;
	connectionType?: string;
	detail?: string;
	downlink?: number;
	failedMethod?: string;
	failedUrl?: string;
	// endpoint that never answered.
	httpStatus?: number;
	lineNumber?: number;

	// Connection diagnostics: tell "the user was offline / on a flaky link" apart
	message: string;
	name: string;
	// from an actual application bug. Key for diagnosing "Failed to fetch" reports.
	online?: boolean;
	rtt?: number;

	// Populated when the error is an HttpClientError: which request failed and why.
	// For network-level failures (fetch threw) httpStatus is 0 and failedUrl is the
	stack: string;
	url?: string;
	userAgent?: string;
	viewportSize?: string;
}

interface NavigatorConnection {
	downlink?: number;
	effectiveType?: string;
	rtt?: number;
}

/**
 * Serializes an error (with its cause chain and browser context) and POSTs it to the given endpoint.
 * Failures to report are swallowed so error logging never throws into the caller.
 * @param url The endpoint that receives the JSON error payload.
 * @param error The thrown value; non-`Error` values are normalized via `ensureErrorType`.
 */
export function logErrorToServer(url: string, error: unknown): void {
	fetch(url, {
		body: JSON.stringify(errorToErrorEntry(ensureErrorType(error))),
		headers: {"Content-Type": "application/json"},
		method: "POST",
		credentials: "include"
	}).catch((_) => {
	});
}
